/**
 * iter84.59 — Recompute Team Stats card (permanent fix for the prod
 * "leaderboard vs The Ride divergence" bug observed on the AGL KO
 * Edition tournament on 2026-02-29).
 *
 * Symptom: a match is recorded (visible in matches collection, The Ride
 * chart shows the rank shift) but the leaderboard's per-team rows stay
 * stuck at 0 because `team.stage_reached` wasn't bumped during the
 * in-place mutation step of `_apply_match` / `apply_knockout_advance`
 * (concurrent poller race, stale snapshot, interrupted write, etc).
 *
 * Fix: call `recompute_team_stats(tournament_id)` directly. Zeroes every
 * team's stats and replays `matches` chronologically through
 * `apply_group_stats` / `apply_knockout_advance`. Strictly idempotent —
 * healthy tournaments are a no-op.
 *
 * Dry-run support: previews what would change without writing. Useful
 * before pulling the trigger on a tournament that LOOKS broken to be
 * sure the recompute actually moves the needle.
 */
import { useState } from "react";
import { RefreshCcw, AlertTriangle, CheckCircle2, FlaskConical } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function SiteAdminRecomputeTeamStatsCard() {
    const [tournamentId, setTournamentId] = useState("");
    const [busy, setBusy] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const handleRun = async (dryRun) => {
        const tid = tournamentId.trim();
        if (!tid) {
            toast.error("Paste a tournament ID first.");
            return;
        }
        if (!dryRun) {
            const ok = window.confirm(
                `Rebuild team.stage_reached + group W/D/L + eliminated for tournament ${tid} ` +
                `from the matches collection?\n\n` +
                `This is idempotent — healthy tournaments are a no-op. Use this when ` +
                `the leaderboard shows 0 points but The Ride chart shows rank shifts ` +
                `(stage_reached fell out of sync with the matches collection).`,
            );
            if (!ok) return;
        }
        setBusy(true);
        setLastResult(null);
        try {
            const res = await api.siteRecomputeTeamStats(tid, dryRun);
            setLastResult({ ...res, dry_run: dryRun });
            const action = dryRun ? "Preview" : "Recompute";
            toast.success(
                `${action} complete: ${res.teams_recomputed} team(s) rebuilt from ` +
                `${res.matches_surviving} match(es)` +
                (res.matches_deleted ? `, ${res.matches_deleted} duplicate(s) removed.` : "."),
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Recompute failed");
            setLastResult({ error: e?.response?.data?.detail || String(e) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="site-recompute-team-stats-card">
            <div className="flex items-start gap-2">
                <RefreshCcw className="w-4 h-4 text-cyber shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100">
                        Leaderboard Parity — Recompute Team Stats
                    </h3>
                    <p className="text-xs text-zinc-400 mt-1">
                        Rebuilds <code className="text-[10px] bg-black/40 px-1 rounded">team.stage_reached</code>,
                        group W/D/L, GF/GA, and eliminated flags by replaying every
                        match in chronological order. Use when the leaderboard shows
                        0 points for a team that clearly advanced (The Ride chart
                        shows the rank shift but per-team rows are stuck).
                        Idempotent on healthy tournaments.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider">
                        Tournament ID
                    </label>
                    <input
                        type="text"
                        value={tournamentId}
                        onChange={(e) => setTournamentId(e.target.value)}
                        placeholder="Paste tournament_id"
                        className="w-full mt-1 bg-black/40 border border-zinc-700 text-xs text-zinc-300 px-2 py-1.5 rounded"
                        data-testid="site-recompute-team-stats-tournament-input"
                    />
                </div>
                <button
                    type="button"
                    onClick={() => handleRun(true)}
                    disabled={busy || !tournamentId.trim()}
                    className="brutal-button px-3 py-2 text-xs uppercase tracking-wider flex items-center gap-1"
                    data-testid="site-recompute-team-stats-dry-run-btn"
                    title="Preview what would change without writing"
                >
                    <FlaskConical className="w-3 h-3" />
                    {busy ? "Running…" : "Dry-run"}
                </button>
                <button
                    type="button"
                    onClick={() => handleRun(false)}
                    disabled={busy || !tournamentId.trim()}
                    className="brutal-button px-3 py-2 text-xs uppercase tracking-wider bg-cyber/20 border-cyber"
                    data-testid="site-recompute-team-stats-run-btn"
                >
                    {busy ? "Recomputing…" : "Recompute now"}
                </button>
            </div>

            {lastResult?.error && (
                <div className="text-xs text-rose-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {lastResult.error}
                </div>
            )}

            {lastResult && !lastResult.error && (
                <div
                    className="text-xs space-y-1 bg-zinc-900/40 border border-zinc-700/40 rounded p-2"
                    data-testid="site-recompute-team-stats-result"
                >
                    <div className="flex items-center gap-2 text-emerald-300">
                        <CheckCircle2 className="w-3 h-3" />
                        <strong className="uppercase tracking-wider">
                            {lastResult.dry_run ? "Dry-run preview" : "Recompute applied"}
                        </strong>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-zinc-300 pl-5">
                        <div>Teams rebuilt: <span className="font-mono text-zinc-100">{lastResult.teams_recomputed}</span></div>
                        <div>Matches replayed: <span className="font-mono text-zinc-100">{lastResult.matches_surviving}</span></div>
                        <div>Duplicates {lastResult.dry_run ? "found" : "removed"}: <span className="font-mono text-zinc-100">{lastResult.matches_deleted ?? lastResult.duplicate_ids?.length ?? 0}</span></div>
                        <div>Tournament: <span className="font-mono text-[10px] text-zinc-500">{lastResult.tournament_id}</span></div>
                    </div>
                    {lastResult.dry_run && (
                        <p className="text-[10px] text-amber-300 italic pl-5 mt-1">
                            Dry-run only — nothing was written. Click "Recompute now" to apply.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
